import { ofType } from 'redux-observable';
import { from, Observable } from 'rxjs';
import { ignoreElements, map, mergeMap } from 'rxjs/operators';

import { ActionManagerInstance } from '../../../actions/manager';
import TargetMap from '../../../actions/targetmap';
import { publicConstants } from '../../../constants';
import { getSemanticURL, resolveRestApi } from '../../../router/app-router';
import { getResolvedTargetName } from '../../../router/routing-manager';
import { fetch } from '../../../service_broker/fetch';
import { getTranslatedDXAPIResponse } from '../../../utils/dxapi-response-translator';
import { logAndCatchError } from '../../../utils/rxjs-utils';
import * as actionType from '../actions';
import * as StateManager from '../state-manager';
import type { Action } from '../state-manager';
import RouteParser from '../../../router/route-parser';
import type { DxAPIResponseObject } from '../../../case/types';
import type { InsightActionConfig } from '../types';

import {
  addPreviewResetAction,
  getActionBasedOnContainerMode,
  getActionPayload,
  getContext,
  processDataToDispatchErrorAction,
  processViewResponse
} from './util';

const showInsight = (action$: Observable<Action>) => {
  // Need to define these in this scope so they can be set/used
  //  in various parts of the processing.
  let actionMgrID = 0;
  let theReturn: Action[] = [];
  return action$.pipe(
    ofType(actionType.SHOW_INSIGHT),
    map((action: Action) => {
      // extract the Action Manager ID from the payload

      ({ actionMgrID } = action.payload);
      return action.payload;
    }),
    mergeMap((payload: Action['payload']) => {
      const { context, skipSemanticUrl = false, insightID } = payload;
      let semanticURL = '';
      let infoParam;

      if (!skipSemanticUrl) {
        semanticURL = getSemanticURL(actionType.ACTION_SHOWINSIGHT, { insightID: '{insightID}' });
        semanticURL = semanticURL.replace('{insightID}', insightID);
        infoParam = RouteParser.getEndPointInfo(semanticURL);
      }

      const { restApi: restUrl, reqType } = resolveRestApi(actionType.ACTION_SHOWINSIGHT, {
        ...(infoParam?.payload || []),
        insightID
      });

      return from(
        fetch(
          restUrl,
          {
            method: reqType
          },
          context
        )
      ).pipe(
        map((response) => {
          let respJsonData = {};
          if (typeof response.data === 'string') {
            respJsonData = JSON.parse(response.data);
          } else {
            respJsonData = response.data;
          }
          return {
            response: {
              ...respJsonData,
              key: semanticURL,
              semanticURL
            },
            actionConfig: payload,
            skipSemanticUrl
          };
        }),
        logAndCatchError(': Error occurred during ajax call at showPage Epic : ', payload.context)
      );
    }),
    mergeMap(
      async (data: {
        type: string;
        payload?: Action['payload'];
        actionMgrID?: number;
        actionConfig: InsightActionConfig;
        skipSemanticUrl: boolean;
        response: DxAPIResponseObject;
      }) => {
        if (data && data.type === actionType.ERROR) {
          // processDataToDispatchErrorAction calls ActionManagerInstance.rejectActionByID
          theReturn.push(processDataToDispatchErrorAction(data));
          return theReturn;
        }

        const { actionConfig, skipSemanticUrl } = data;

        const containerName = TargetMap.getTargetViewContainerName(actionType.SHOW_PAGE);
        const target = getResolvedTargetName(containerName, actionConfig.context);
        const context = getContext(target, containerName, actionConfig.context);
        const { response } = data;
        const translatedDXAPIResponse = getTranslatedDXAPIResponse(response);
        translatedDXAPIResponse.directUrlUpdation = !skipSemanticUrl;
        const options = { acName: target, context };
        const isSemanticURLAssociatedWithActivateContainer = !skipSemanticUrl;
        const skipParseResponse = skipSemanticUrl;
        await processViewResponse(
          translatedDXAPIResponse,
          actionConfig,
          options,
          isSemanticURLAssociatedWithActivateContainer,
          skipParseResponse
        );
        const commonInfo = { target, context, containerItemID: context };
        const payload = getActionPayload(translatedDXAPIResponse, commonInfo);
        payload.resourceType = publicConstants.PAGE;
        const action = getActionBasedOnContainerMode(target, payload);
        theReturn.push(action);
        addPreviewResetAction(theReturn);
        return theReturn;
      }
    ),
    map(() => {
      // this is the right place to note that the promise should resolve...
      ActionManagerInstance.resolveActionByID(actionMgrID);
      StateManager.dispatch(theReturn);
      theReturn = [];
    }),
    ignoreElements()
  );
};

const insightEpics = [showInsight];

export default insightEpics;
